import { isDemoEnv } from 'lib/environment/environment'
import { useSaveProducts } from 'modules/Cube/communication/external/billingSchedule.api.v1/ports/entitySaving/useSaveProducts'
import { useSaveQuote } from 'modules/Cube/communication/external/quotes.api.v1/ports/entitySaving'
import {
  ApiClientCreatedQuoteModel,
  ApiClientUpdatedQuoteModel,
  ApiClientQuoteModel,
  ApiClientArchivedQuote,
  ApiClientPublishQuoteOutcome,
  PublishQuoteData,
  QuoteExecuteScheduleAction,
  v1ApiUpdateQuote
} from 'modules/Cube/communication/external/quotes.api.v1/ports/entitySaving/useSaveQuote'
import { Product } from 'modules/Cube/domain/cube.domain.types'
import { useCallback } from 'react'

type UseSaveQuoteEditor = () => {
  acceptQuote: (id: string) => Promise<{
    success: boolean
    quote: ApiClientUpdatedQuoteModel | null
  }>
  createQuote: () => Promise<{
    success: boolean
    quote: ApiClientCreatedQuoteModel | null
  }>
  executeQuote: (
    id: string,
    scheduleAction: QuoteExecuteScheduleAction
  ) => Promise<{
    success: boolean
    quote: ApiClientUpdatedQuoteModel | null
  }>
  publishQuote: (
    id: string,
    data: PublishQuoteData
  ) => Promise<{
    success: boolean
    outcome: ApiClientPublishQuoteOutcome | null
  }>
  updateQuote: (id: string) => (
    data: v1ApiUpdateQuote,
    products: Product[]
  ) => Promise<{
    success: boolean
    quote: ApiClientQuoteModel | null
  }>
  archiveQuote: (id: string) => Promise<{
    success: boolean
    quote: ApiClientArchivedQuote | null
  }>
  deleteLatestQuoteDraft: (id: string) => Promise<{
    success: boolean
    quote: ApiClientQuoteModel | null
  }>
  duplicateQuote: (id: string) => Promise<{
    success: boolean
    quote: ApiClientQuoteModel | null
  }>
}

const applyNewProductsToPhases =
  (quoteMutation: v1ApiUpdateQuote) =>
  (newProducts: { temporaryId: string; realId: string }[]) => {
    const phasesWithUpdatedPrices = quoteMutation.phases.map(phase => {
      const updatedPrices = phase.prices.map(price => {
        const newProduct = newProducts.find(
          product => product.temporaryId === price.productId
        )

        if (newProduct) {
          return {
            ...price,
            productId: newProduct.realId
          }
        }
        return price
      })

      return {
        ...phase,
        prices: updatedPrices
      }
    })

    return phasesWithUpdatedPrices
  }

export const applyNewProductsToPresentation =
  (quoteMutation: v1ApiUpdateQuote) =>
  (newProducts: { temporaryId: string; realId: string }[]) => {
    const productOrder =
      quoteMutation.presentation.settings?.priceEditorProductOrder

    if (!productOrder) {
      return quoteMutation.presentation
    }

    return {
      ...quoteMutation.presentation,
      settings: {
        ...quoteMutation.presentation.settings,
        priceEditorProductOrder: productOrder.map(
          (productId: string) =>
            newProducts.find(product => product.temporaryId === productId)
              ?.realId ?? productId
        )
      }
    }
  }

/**
 * Autosave and the header can both try to create the same pending product.
 * Cache the creation promise per temp id so a second caller reuses it.
 */
const productCreationCache = new Map<string, Promise<string | null>>()

const createProductOnce = (
  productsMutator: Pick<ReturnType<typeof useSaveProducts>, 'createProducts'>,
  product: Product
): Promise<string | null> => {
  const cached = productCreationCache.get(product.id)

  if (cached) {
    return cached
  }

  const creation = productsMutator
    .createProducts([product])
    .then(([result]) => result?.realId ?? null)
    .catch(() => null)
    .then(realId => {
      // Don't cache a failure — let the next attempt retry the creation.
      if (!realId) {
        productCreationCache.delete(product.id)
      }
      return realId
    })

  productCreationCache.set(product.id, creation)

  return creation
}

/**
 * Creates new products (see `createProductOnce` for dedup) and remaps
 * temporary ids in phases and the saved order. Returns null on failure.
 */
export const resolveNewQuoteProducts = async (
  productsMutator: Pick<ReturnType<typeof useSaveProducts>, 'createProducts'>,
  data: v1ApiUpdateQuote,
  products: Product[]
): Promise<v1ApiUpdateQuote | null> => {
  const newProducts = await Promise.all(
    products.map(async product => ({
      temporaryId: product.id,
      realId: await createProductOnce(productsMutator, product)
    }))
  )

  if (newProducts.some(({ realId }) => !realId)) {
    return null
  }

  // TS is being annoying and insists `realId` can be null here despite the check earlier
  const productsMapping = newProducts as {
    temporaryId: string
    realId: string
  }[]

  return {
    ...data,
    phases: applyNewProductsToPhases(data)(productsMapping),
    presentation: applyNewProductsToPresentation(data)(productsMapping)
  }
}

export const useSaveQuoteEditor: UseSaveQuoteEditor = () => {
  const quoteMutator = useSaveQuote()
  const productsMutator = useSaveProducts()

  const updateQuote = useCallback(
    (id: string) => async (data: v1ApiUpdateQuote, products: Product[]) => {
      if (isDemoEnv()) {
        return {
          quote: null,
          success: true
        }
      }

      const resolvedData = await resolveNewQuoteProducts(
        productsMutator,
        data,
        products
      )

      if (resolvedData === null) {
        return {
          quote: null,
          success: false
        }
      }

      const updatedQuote = await quoteMutator.update(id, resolvedData)

      if (updatedQuote === null) {
        return {
          success: false,
          quote: null
        }
      }

      return {
        success: true,
        quote: updatedQuote
      }
    },
    [productsMutator, quoteMutator]
  )

  const createQuote = useCallback(async () => {
    const newQuote = await quoteMutator.create()

    if (!newQuote) {
      return {
        success: false,
        quote: null
      }
    }

    return {
      success: true,
      quote: newQuote
    }
  }, [quoteMutator])

  const publishQuote = useCallback(
    async (quoteId: string, data: PublishQuoteData) => {
      if (isDemoEnv()) {
        return {
          outcome: null,
          success: true
        }
      }

      const outcome = await quoteMutator.publishQuote(quoteId, data)

      if (!outcome) {
        throw new Error('Error publishing quote')
      }

      return {
        success: true,
        outcome
      }
    },
    [quoteMutator]
  )

  const acceptQuote = useCallback(
    async (quoteId: string) => {
      const acceptedQuote = await quoteMutator.accept(quoteId)

      if (!acceptedQuote) {
        throw new Error('Error accepting quote')
      }

      return {
        success: true,
        quote: acceptedQuote
      }
    },
    [quoteMutator]
  )

  const archiveQuote = useCallback(
    async (quoteId: string) => {
      const archivedQuote = await quoteMutator.archive(quoteId)

      if (!archivedQuote) {
        throw new Error('Error archiving quote')
      }

      return {
        success: true,
        quote: archivedQuote
      }
    },
    [quoteMutator]
  )

  const deleteLatestQuoteDraft = useCallback(
    async (quoteId: string) => {
      const updatedQuote = await quoteMutator.deleteLatestDraft(quoteId)

      if (!updatedQuote) {
        throw new Error('Error updating quote')
      }

      return {
        success: true,
        quote: updatedQuote
      }
    },
    [quoteMutator]
  )

  const executeQuote = useCallback(
    async (quoteId: string, scheduleAction: QuoteExecuteScheduleAction) => {
      const executedQuote = await quoteMutator.execute(quoteId, scheduleAction)

      if (!executedQuote) {
        throw new Error('Error executing quote')
      }

      return {
        success: true,
        quote: executedQuote
      }
    },
    [quoteMutator]
  )

  const duplicateQuote = useCallback(
    async (quoteId: string) => {
      const duplicatedQuote = await quoteMutator.duplicate(quoteId)

      if (!duplicatedQuote) {
        throw new Error('Error duplicating quote')
      }

      return {
        success: true,
        quote: duplicatedQuote
      }
    },
    [quoteMutator]
  )

  return {
    acceptQuote,
    createQuote,
    executeQuote,
    publishQuote,
    updateQuote,
    archiveQuote,
    deleteLatestQuoteDraft,
    duplicateQuote
  }
}
